import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Send, Calendar, CheckCircle2, XCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Settings card: toggle the Monday-morning Missing-CTC Slack digest.
 *
 * Pairs with the existing org Slack-webhook setting (the digest is a no-op
 * when no webhook is configured). Admins can also fire a "Send test now"
 * to verify the message lands before Monday rolls around.
 */
const MissingCtcDigestCard = () => {
  const [config, setConfig] = useState({ enabled: false, last_run_at: null, schedule_label: '' });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/organizations/me/missing-ctc-digest`)
      .then((res) => setConfig(res.data || {}))
      .catch(() => { /* ignore — non-admin gets 403 and card simply stays default */ })
      .finally(() => setLoaded(true));
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    try {
      await axios.put(`${API}/organizations/me/missing-ctc-digest`, { enabled: next });
      setConfig((prev) => ({ ...prev, enabled: next }));
      toast.success(next ? 'Monday digest enabled' : 'Monday digest disabled');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/organizations/me/missing-ctc-digest/test`);
      const { ok, reason, offered_count, joined_count, slack_ok } = res.data || {};
      if (!ok) {
        toast.info(
          reason === 'no_missing'
            ? 'Nothing to digest — every Offered/Joined application already has CTC on file.'
            : 'No Slack webhook configured. Connect Slack above first.',
        );
      } else if (slack_ok) {
        toast.success(`Digest sent · ${offered_count} offered · ${joined_count} joined`);
      } else {
        toast.warning(`Digest computed (${offered_count} offered · ${joined_count} joined) but Slack delivery failed. Check your webhook.`);
      }
      // Refresh last_run_at
      const fresh = await axios.get(`${API}/organizations/me/missing-ctc-digest`);
      setConfig(fresh.data || config);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setTesting(false);
    }
  };

  if (!loaded) return null;

  return (
    <Card data-testid="missing-ctc-digest-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-amber-600" />
          Missing-CTC Monday digest
        </CardTitle>
        <CardDescription>
          Every Monday at 9:00 AM IST, we'll ping your Slack channel with the count of
          Offered + Joined applications still missing CTC, plus the worst 5 to tackle
          first. Stays quiet when there's nothing to flag — no message means clean data.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          {config.enabled ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Enabled
            </Badge>
          ) : (
            <Badge variant="outline" className="text-slate-600">
              <XCircle className="w-3 h-3 mr-1" /> Disabled
            </Badge>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            {config.schedule_label || 'Every Monday at 9:00 AM IST'}
          </span>
          {config.last_run_at ? (
            <span className="text-[11px] text-slate-500">
              Last run: {format(new Date(config.last_run_at), 'dd MMM yyyy, HH:mm')}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => toggle(!config.enabled)}
            disabled={saving}
            data-testid="missing-ctc-digest-toggle-btn"
            className={config.enabled ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-teal-700 hover:bg-teal-800 text-white'}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : null}
            {config.enabled ? 'Disable digest' : 'Enable digest'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={sendTest}
            disabled={testing}
            data-testid="missing-ctc-digest-test-btn"
          >
            {testing ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1.5" />
            )}
            Send test now
          </Button>
        </div>
        <p className="text-[11px] text-slate-500">
          Pre-req: Slack Incoming Webhook configured above. The digest is a no-op when no
          webhook is connected.
        </p>
      </CardContent>
    </Card>
  );
};

export default MissingCtcDigestCard;
